import React, { useState, useEffect } from 'react';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const NotificationSettings: React.FC = () => {
  const {
    status: pushStatus,
    preferences,
    subscribe,
    unsubscribe,
    loadPreferences,
    updatePreferences,
    sendTest,
    debugInfo
  } = usePushNotifications('admin');

  const [testingPush, setTestingPush] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; result: any } | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pushStatus.isSupported && pushStatus.isSubscribed) {
      loadPreferences();
    }
  }, [pushStatus.isSupported, pushStatus.isSubscribed, loadPreferences]);

  const handleSubscribePush = async () => {
    const success = await subscribe();
    if (success) {
      loadPreferences();
    } else if (pushStatus.error) {
      setError(pushStatus.error);
    }
  };

  const handleUnsubscribePush = async () => {
    await unsubscribe();
  };

  const handleTestNotification = async () => {
    setTestingPush(true);
    setTestResult(null);
    try {
      const result = await sendTest();
      setTestResult(result);
      if (!result.success) {
        setError(`Test failed: ${JSON.stringify(result.result)}`);
      }
    } catch (err) {
      console.error('Error sending test push:', err);
      setError('Failed to send test notification');
      setTestResult({ success: false, result: { error: String(err) } });
    } finally {
      setTestingPush(false);
    }
  };

  const handlePreferenceChange = async (key: string, value: boolean) => {
    await updatePreferences({ [key]: value });
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>Notification Settings</h1>

      {/* Push Notifications Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>🔔</span>
          Push Notifications
        </h2>
        <p style={styles.description}>
          Receive alerts for chat messages, order updates, and low inventory on your device.
        </p>

        {error && (
          <div style={styles.errorMessage}>
            {error}
            <button style={styles.dismissBtn} onClick={() => setError(null)}>×</button>
          </div>
        )}

        {!pushStatus.isSupported ? (
          <div style={styles.notAvailable}>
            <p>Push notifications are not supported in this browser.</p>
            <p>Try using Safari on iPhone/Mac, Chrome on Android, or Edge on Windows.</p>
          </div>
        ) : (
          <>
            {/* Enable/Disable Toggle */}
            <div style={styles.toggleRow}>
              <div>
                <span style={styles.toggleLabel}>Enable Push Notifications</span>
                <span style={styles.toggleHint}>Receive alerts on this device</span>
              </div>
              <button
                style={{
                  ...styles.toggleBtn,
                  backgroundColor: pushStatus.isSubscribed ? '#22c55e' : '#e5e7eb',
                }}
                onClick={pushStatus.isSubscribed ? handleUnsubscribePush : handleSubscribePush}
                disabled={pushStatus.isLoading}
              >
                <span style={{
                  ...styles.toggleThumb,
                  transform: pushStatus.isSubscribed ? 'translateX(24px)' : 'translateX(0)',
                }} />
              </button>
            </div>

            {/* Notification Preferences */}
            {pushStatus.isSubscribed && preferences && (
              <>
                <div style={styles.divider} />
                <h3 style={styles.subTitle}>Notification Types</h3>

                <div style={styles.toggleRow}>
                  <div>
                    <span style={styles.toggleLabel}>Chat Messages</span>
                    <span style={styles.toggleHint}>Get notified of new team messages</span>
                  </div>
                  <button
                    style={{
                      ...styles.toggleBtn,
                      backgroundColor: preferences.feedmill_chat_messages ? '#22c55e' : '#e5e7eb',
                    }}
                    onClick={() => handlePreferenceChange('feedmill_chat_messages', !preferences.feedmill_chat_messages)}
                  >
                    <span style={{
                      ...styles.toggleThumb,
                      transform: preferences.feedmill_chat_messages ? 'translateX(24px)' : 'translateX(0)',
                    }} />
                  </button>
                </div>

                <div style={styles.toggleRow}>
                  <div>
                    <span style={styles.toggleLabel}>Order Status Changes</span>
                    <span style={styles.toggleHint}>Updates when orders change status</span>
                  </div>
                  <button
                    style={{
                      ...styles.toggleBtn,
                      backgroundColor: preferences.feedmill_order_status ? '#22c55e' : '#e5e7eb',
                    }}
                    onClick={() => handlePreferenceChange('feedmill_order_status', !preferences.feedmill_order_status)}
                  >
                    <span style={{
                      ...styles.toggleThumb,
                      transform: preferences.feedmill_order_status ? 'translateX(24px)' : 'translateX(0)',
                    }} />
                  </button>
                </div>

                <div style={styles.toggleRow}>
                  <div>
                    <span style={styles.toggleLabel}>Low Inventory Alerts</span>
                    <span style={styles.toggleHint}>Warning when items run low</span>
                  </div>
                  <button
                    style={{
                      ...styles.toggleBtn,
                      backgroundColor: preferences.feedmill_low_inventory ? '#22c55e' : '#e5e7eb',
                    }}
                    onClick={() => handlePreferenceChange('feedmill_low_inventory', !preferences.feedmill_low_inventory)}
                  >
                    <span style={{
                      ...styles.toggleThumb,
                      transform: preferences.feedmill_low_inventory ? 'translateX(24px)' : 'translateX(0)',
                    }} />
                  </button>
                </div>

                {/* Test Button */}
                <div style={styles.divider} />
                <button
                  style={styles.testButton}
                  onClick={handleTestNotification}
                  disabled={testingPush}
                >
                  {testingPush ? 'Sending...' : '🔔 Send Test Notification'}
                </button>

                {testResult && (
                  <div style={{
                    ...styles.testResult,
                    backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
                    borderColor: testResult.success ? '#86efac' : '#fecaca',
                    color: testResult.success ? '#166534' : '#dc2626',
                  }}>
                    {testResult.success ? (
                      <span>✓ Notification sent! Check your device.</span>
                    ) : (
                      <span>✗ Failed: {testResult.result?.error || testResult.result?.message || 'No subscriptions'}</span>
                    )}
                    <pre style={styles.testResultPre}>
                      {JSON.stringify(testResult.result, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {/* Debug Info */}
            <div style={styles.divider} />
            <button
              style={styles.debugToggle}
              onClick={() => setShowDebug(!showDebug)}
            >
              {showDebug ? 'Hide' : 'Show'} Debug Info
            </button>

            {showDebug && (
              <div style={styles.debugPanel}>
                <p><strong>Push Status:</strong></p>
                <ul style={styles.debugList}>
                  <li>Supported: {pushStatus.isSupported ? 'Yes' : 'No'}</li>
                  <li>Permission: {pushStatus.permission}</li>
                  <li>Subscribed: {pushStatus.isSubscribed ? 'Yes' : 'No'}</li>
                  <li>SW Registered: {pushStatus.swRegistered ? 'Yes' : 'No'}</li>
                  <li>SW State: {pushStatus.swState || 'N/A'}</li>
                  <li>VAPID Key: {pushStatus.vapidKeyLoaded ? 'Loaded' : 'Not loaded'}</li>
                  {pushStatus.error && <li style={{ color: 'red' }}>Error: {pushStatus.error}</li>}
                </ul>
                <p style={{ marginTop: '8px' }}><strong>Debug Log:</strong></p>
                <div style={styles.debugLog}>
                  {debugInfo.map((line, i) => (
                    <div key={i} style={styles.debugLogLine}>{line}</div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: '2rem',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 0.5rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  icon: {
    fontSize: '1.25rem',
  },
  description: {
    color: '#6b7280',
    marginBottom: '1.5rem',
  },
  notAvailable: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '1rem',
    color: '#92400e',
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#dc2626',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.25rem',
    cursor: 'pointer',
    color: '#dc2626',
    padding: '0 0.5rem',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 0',
    borderBottom: '1px solid #f3f4f6',
  },
  toggleLabel: {
    display: 'block',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  toggleHint: {
    display: 'block',
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  toggleBtn: {
    width: '52px',
    height: '28px',
    borderRadius: '14px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative' as const,
    transition: 'background-color 0.2s',
  },
  toggleThumb: {
    display: 'block',
    width: '24px',
    height: '24px',
    borderRadius: '12px',
    backgroundColor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    position: 'absolute' as const,
    top: '2px',
    left: '2px',
    transition: 'transform 0.2s',
  },
  divider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    margin: '1.5rem 0',
  },
  subTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '1rem',
  },
  testButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    width: '100%',
  },
  testResult: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid',
    marginTop: '1rem',
    fontSize: '0.9rem',
  },
  testResultPre: {
    fontSize: '10px',
    marginTop: '8px',
    whiteSpace: 'pre-wrap' as const,
    fontFamily: 'monospace',
  },
  debugToggle: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: 0,
  },
  debugPanel: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    fontSize: '11px',
    fontFamily: 'monospace',
  },
  debugList: {
    margin: '4px 0',
    paddingLeft: '20px',
  },
  debugLog: {
    maxHeight: '150px',
    overflow: 'auto',
    backgroundColor: '#fff',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  debugLogLine: {
    borderBottom: '1px solid #eee',
    padding: '2px 0',
  },
};

export default NotificationSettings;
